<template>
  <div class="account-heads-page">
    <vue-element-loading
      :active="appLoading"
      :is-full-screen="true"
      background-color="#FFFFFF"
      color="#9a6b27"
      spinner="bar-fade-scale"
    />
    <ServerError v-if="ServerError" />
    <v-snackbar v-model="showsnackbar" color="#9a6b27" right :timeout="timeout">
      <v-layout wrap justify-center>
        <v-flex text-left class="align-self-center">
          <span style="color: white">{{ msg }}</span>
        </v-flex>
        <v-flex text-right>
          <v-btn small :ripple="false" text @click="showsnackbar = false">
            <v-icon style="color: #ffffff">mdi-close</v-icon>
          </v-btn>
        </v-flex>
      </v-layout>
    </v-snackbar>

    <!-- Page Header: Breadcrumbs, Title, Subtitle, & Actions -->
    <div class="page-header-wrap mb-4">
      <div class="d-flex flex-wrap align-center justify-space-between">
        <!-- Left: Breadcrumb + Title + Subtitle -->
        <div class="header-text-block">
          <PageBreadcrumb :items="[{ text: 'Accounts', to: '/Accounts' }, { text: 'Account Heads' }]" class="mb-1" />
          <p class="page-subtitle mb-0">Manage financial chart of accounts, asset classifications, and ledger heads.</p>
        </div>

        <!-- Right: Header Actions -->
        <div class="header-actions-group d-flex flex-wrap align-center mt-3 mt-sm-0">
          <div class="head-count-badge mr-3" v-if="headList">
            <v-icon size="14" color="#9a6b27" class="mr-1">mdi-format-list-bulleted</v-icon>
            <span>{{ headList.length }} Account Heads</span>
          </div>

          <v-btn
            outlined
            depressed
            class="btn-action-outline mr-2"
            @click="$router.push('/Accounts')"
          >
            <v-icon left size="16" class="btn-icon">mdi-book-open-outline</v-icon>
            Vouchers
          </v-btn>

          <v-btn
            outlined
            depressed
            class="btn-action-outline btn-icon-only"
            @click="getList()"
            title="Refresh Account Heads"
          >
            <v-icon size="17" color="#64748b">mdi-refresh</v-icon>
          </v-btn>
        </div>
      </div>
    </div>

    <!-- Card 1: Add New Account Head Form -->
    <v-card class="form-card rounded-xl mb-6" elevation="0">
      <div class="form-card-header d-flex align-center justify-space-between px-5 py-3 border-bottom">
        <div class="d-flex align-center">
          <div class="header-icon-pill mr-3">
            <v-icon size="18" color="#9a6b27">mdi-plus-circle-outline</v-icon>
          </div>
          <div>
            <h2 class="section-title mb-0">New Account Head</h2>
            <p class="section-sub mb-0">Add a ledger classification to organize balance sheet and income statement entries</p>
          </div>
        </div>
      </div>

      <div class="form-card-body px-5 py-4">
        <v-row align="end">
          <!-- Name Input -->
          <v-col cols="12" sm="12" md="4" class="py-1">
            <label class="form-field-label">
              ACCOUNT NAME <span class="required-asterisk">*</span>
            </label>
            <v-text-field
              v-model="name"
              outlined
              dense
              hide-details
              placeholder="e.g. HDFC Bank, Rent, Office Supplies"
              class="modern-input-field"
              color="#9a6b27"
              prepend-inner-icon="mdi-card-text-outline"
              @keydown.enter="addHead"
            ></v-text-field>
          </v-col>

          <!-- Code Input -->
          <v-col cols="12" sm="6" md="3" class="py-1">
            <label class="form-field-label">
              ACCOUNT CODE <span class="optional-hint">(Optional)</span>
            </label>
            <v-text-field
              v-model="code"
              outlined
              dense
              hide-details
              placeholder="e.g. C11, AST01"
              class="modern-input-field"
              color="#9a6b27"
              prepend-inner-icon="mdi-pound"
              @keydown.enter="addHead"
            ></v-text-field>
          </v-col>

          <!-- Type Select -->
          <v-col cols="12" sm="6" md="3" class="py-1">
            <label class="form-field-label">
              ACCOUNT TYPE <span class="required-asterisk">*</span>
            </label>
            <v-select
              v-model="type"
              :items="['Asset', 'Liability', 'Capital', 'Income', 'Expense']"
              outlined
              dense
              hide-details
              class="modern-input-field modern-select"
              color="#9a6b27"
              prepend-inner-icon="mdi-shape-outline"
            ></v-select>
          </v-col>

          <!-- Submit Button -->
          <v-col cols="12" sm="12" md="2" class="py-1">
            <v-btn
              depressed
              class="btn-add-head w-100"
              @click="addHead()"
              :loading="appLoading"
            >
              <v-icon left size="16">mdi-plus</v-icon>
              Add Head
            </v-btn>
          </v-col>
        </v-row>
      </div>
    </v-card>

    <!-- Card 2: Chart of Accounts / Account Heads Table -->
    <v-card class="table-card rounded-xl" elevation="0">
      <!-- Table Header Bar with Search & Filters -->
      <div class="table-card-top-bar d-flex flex-wrap align-center justify-space-between px-5 py-3 border-bottom">
        <div class="d-flex align-center my-1">
          <h2 class="section-title mb-0">Chart of Accounts</h2>
          <span class="active-count-pill ml-2" v-if="headList">
            {{ filteredHeadList.length }} {{ filteredHeadList.length === 1 ? 'entry' : 'entries' }}
          </span>
        </div>

        <div class="d-flex flex-wrap align-center gap-2 my-1">
          <!-- Type Filter -->
          <v-select
            v-model="selectedTypeFilter"
            :items="typeFilterOptions"
            outlined
            dense
            hide-details
            class="filter-type-select mr-2"
            color="#9a6b27"
            prepend-inner-icon="mdi-filter-variant"
          ></v-select>

          <!-- Search Input -->
          <v-text-field
            v-model="searchQuery"
            outlined
            dense
            hide-details
            clearable
            placeholder="Search by name or code..."
            class="filter-search-input"
            color="#9a6b27"
            prepend-inner-icon="mdi-magnify"
          ></v-text-field>
        </div>
      </div>

      <!-- Table Body -->
      <div class="table-responsive" v-if="filteredHeadList.length > 0">
        <table class="custom-table">
          <thead>
            <tr>
              <th class="col-sno text-center">#</th>
              <th class="col-name">ACCOUNT NAME</th>
              <th class="col-code">CODE</th>
              <th class="col-type">TYPE</th>
              <th class="col-action text-right">ACTION</th>
            </tr>
          </thead>
          <tbody>
            <tr
              v-for="(value, i) in filteredHeadList"
              :key="value._id || i"
              class="table-row"
            >
              <!-- S.No -->
              <td class="col-sno text-center text-muted font-mono">
                {{ i + 1 }}
              </td>

              <!-- Name with Icon -->
              <td class="col-name">
                <div class="d-flex align-center">
                  <div class="account-icon-box mr-3" :class="getTypeIconBoxClass(value.type)">
                    <v-icon size="15" :color="getTypeColor(value.type)">{{ getTypeIcon(value.type) }}</v-icon>
                  </div>
                  <div>
                    <div class="account-name-text">{{ value.name }}</div>
                    <div class="account-code-sub d-sm-none" v-if="value.code">Code: {{ value.code }}</div>
                  </div>
                </div>
              </td>

              <!-- Code -->
              <td class="col-code">
                <span v-if="value.code" class="code-badge font-mono">{{ value.code }}</span>
                <span v-else class="text-muted-dash">—</span>
              </td>

              <!-- Type Badge -->
              <td class="col-type">
                <span class="type-badge" :class="getTypeBadgeClass(value.type)">
                  <span class="type-dot" :style="{ backgroundColor: getTypeColor(value.type) }"></span>
                  {{ value.type }}
                </span>
              </td>

              <!-- Action -->
              <td class="col-action text-right">
                <span v-if="value.isDefault" class="default-badge">
                  <v-icon size="13" color="#64748b" class="mr-1">mdi-lock-outline</v-icon>
                  Default
                </span>
                <v-btn
                  v-else
                  icon
                  small
                  class="btn-delete-action"
                  @click="deleteHead(value._id, value.name)"
                  title="Delete Account Head"
                >
                  <v-icon size="17" color="#ef4444">mdi-delete-outline</v-icon>
                </v-btn>
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <!-- Empty State -->
      <div v-else class="empty-state-wrap text-center py-10 px-4">
        <div class="empty-icon-circle mx-auto mb-3">
          <v-icon size="36" color="#94a3b8">mdi-book-open-blank-variant-outline</v-icon>
        </div>
        <h3 class="empty-title mb-1">
          {{ searchQuery || selectedTypeFilter !== 'All' ? 'No matching account heads' : 'No account heads found' }}
        </h3>
        <p class="empty-desc mb-4">
          {{ searchQuery || selectedTypeFilter !== 'All'
            ? 'Try adjusting your search query or type filter.'
            : 'Add your first account head using the form above.'
          }}
        </p>
        <v-btn
          v-if="searchQuery || selectedTypeFilter !== 'All'"
          outlined
          small
          class="btn-action-outline"
          @click="resetFilters"
        >
          <v-icon left size="14">mdi-filter-remove-outline</v-icon>
          Reset Filters
        </v-btn>
      </div>

      <!-- Table Footer / Summary Bar -->
      <div class="table-card-footer d-flex align-center justify-space-between px-5 py-3 border-top" v-if="filteredHeadList.length > 0">
        <div class="footer-summary-text">
          Showing <strong>{{ filteredHeadList.length }}</strong> of <strong>{{ headList.length }}</strong> total account heads
        </div>
        <div class="footer-legend d-none d-md-flex align-center gap-3">
          <span class="legend-item"><span class="type-dot bg-asset"></span> Asset</span>
          <span class="legend-item"><span class="type-dot bg-liability"></span> Liability</span>
          <span class="legend-item"><span class="type-dot bg-capital"></span> Capital</span>
          <span class="legend-item"><span class="type-dot bg-income"></span> Income</span>
          <span class="legend-item"><span class="type-dot bg-expense"></span> Expense</span>
        </div>
      </div>
    </v-card>
  </div>
</template>

<script>
import axios from "axios";

export default {
  data() {
    return {
      appLoading: false,
      ServerError: false,
      showsnackbar: false,
      timeout: 2000,
      msg: null,
      name: "",
      code: "",
      type: "Asset",
      headList: [],
      searchQuery: "",
      selectedTypeFilter: "All",
      typeFilterOptions: ["All", "Asset", "Liability", "Capital", "Income", "Expense"],
    };
  },
  computed: {
    filteredHeadList() {
      if (!this.headList) return [];
      let list = this.headList;
      if (this.selectedTypeFilter && this.selectedTypeFilter !== "All") {
        list = list.filter((item) => item.type === this.selectedTypeFilter);
      }
      if (this.searchQuery && this.searchQuery.trim()) {
        const q = this.searchQuery.toLowerCase().trim();
        list = list.filter((item) => {
          const nameMatch = item.name && item.name.toLowerCase().includes(q);
          const codeMatch = item.code && item.code.toLowerCase().includes(q);
          const typeMatch = item.type && item.type.toLowerCase().includes(q);
          return nameMatch || codeMatch || typeMatch;
        });
      }
      return list;
    },
  },
  mounted() {
    this.getList();
  },
  methods: {
    resetFilters() {
      this.searchQuery = "";
      this.selectedTypeFilter = "All";
    },
    getTypeColor(type) {
      switch (type) {
        case "Asset":
          return "#059669"; // emerald
        case "Liability":
          return "#d97706"; // amber
        case "Capital":
          return "#7c3aed"; // violet
        case "Income":
          return "#0d9488"; // teal
        case "Expense":
          return "#e11d48"; // rose
        default:
          return "#64748b";
      }
    },
    getTypeBadgeClass(type) {
      switch (type) {
        case "Asset":
          return "badge-asset";
        case "Liability":
          return "badge-liability";
        case "Capital":
          return "badge-capital";
        case "Income":
          return "badge-income";
        case "Expense":
          return "badge-expense";
        default:
          return "badge-default";
      }
    },
    getTypeIcon(type) {
      switch (type) {
        case "Asset":
          return "mdi-bank-outline";
        case "Liability":
          return "mdi-scale-balance";
        case "Capital":
          return "mdi-safe";
        case "Income":
          return "mdi-trending-up";
        case "Expense":
          return "mdi-receipt-text-outline";
        default:
          return "mdi-folder-outline";
      }
    },
    getTypeIconBoxClass(type) {
      switch (type) {
        case "Asset":
          return "icon-box-asset";
        case "Liability":
          return "icon-box-liability";
        case "Capital":
          return "icon-box-capital";
        case "Income":
          return "icon-box-income";
        case "Expense":
          return "icon-box-expense";
        default:
          return "icon-box-default";
      }
    },
    getList() {
      this.appLoading = true;
      axios({
        method: "get",
        url: "/accounthead/getlist",
        headers: { "x-auth-token": localStorage.getItem("token") },
      })
        .then((response) => {
          this.appLoading = false;
          if (response.data.status == true) {
            this.headList = response.data.data;
          } else {
            this.msg = response.data.msg;
            this.showsnackbar = true;
          }
        })
        .catch((err) => {
          this.appLoading = false;
          this.ServerError = true;
          console.log(err);
        });
    },
    addHead() {
      if (!this.name || !this.name.trim()) {
        this.msg = "Please enter an account name";
        this.showsnackbar = true;
        return;
      }
      this.appLoading = true;
      axios({
        method: "post",
        url: "/accounthead/add",
        headers: { "x-auth-token": localStorage.getItem("token") },
        data: { name: this.name.trim(), code: this.code ? this.code.trim() : "", type: this.type },
      })
        .then((response) => {
          this.appLoading = false;
          this.msg = response.data.msg;
          this.showsnackbar = true;
          if (response.data.status == true) {
            this.name = "";
            this.code = "";
            this.getList();
          }
        })
        .catch((err) => {
          this.appLoading = false;
          this.ServerError = true;
          console.log(err);
        });
    },
    async deleteHead(id, name) {
      const targetName = name ? ` "${name}"` : "";
      if (this.$confirm) {
        const ok = await this.$confirm(
          `Are you sure you want to delete the account head${targetName}? This action cannot be undone.`,
          {
            title: "Delete Account Head",
            confirmText: "Delete",
          }
        );
        if (!ok) return;
      }
      this.appLoading = true;
      axios({
        method: "post",
        url: "/accounthead/delete",
        headers: { "x-auth-token": localStorage.getItem("token") },
        data: { id: id },
      })
        .then((response) => {
          this.appLoading = false;
          this.msg = response.data.msg;
          this.showsnackbar = true;
          if (response.data.status == true) {
            this.getList();
          }
        })
        .catch((err) => {
          this.appLoading = false;
          console.log(err);
        });
    },
  },
};
</script>

<style scoped>
@import url("https://fonts.googleapis.com/css2?family=Plus+Jakarta+Sans:ital,wght@0,300;0,400;0,500;0,600;0,700;0,800;1,400;1,500&family=Inter:wght@400;500;600;700&display=swap");

.account-heads-page {
  font-family: "Plus Jakarta Sans", "Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif;
  color: #1e293b;
  padding-bottom: 32px;
}

/* Page Header */
.page-header-wrap {
  padding: 2px 0;
}

.breadcrumbs-row {
  font-family: "Plus Jakarta Sans", sans-serif;
  font-size: 12px;
  color: #64748b;
  font-weight: 500;
  letter-spacing: -0.01em;
}

.breadcrumb-sep {
  color: #94a3b8;
  margin: 0 6px;
  font-weight: 300;
}

.breadcrumb-link {
  color: #64748b !important;
  text-decoration: none;
  transition: color 0.15s ease;
}

.breadcrumb-link:hover {
  color: #9a6b27 !important;
}

.breadcrumb-item.active {
  color: #9a6b27;
  font-weight: 600;
}

.page-main-title {
  font-family: "Plus Jakarta Sans", -apple-system, BlinkMacSystemFont, sans-serif;
  font-size: 25px;
  font-weight: 700;
  color: #0f172a;
  line-height: 1.25;
  margin: 0;
  letter-spacing: -0.025em;
}

.page-subtitle {
  font-family: "Plus Jakarta Sans", sans-serif;
  font-size: 13.5px;
  color: #64748b;
  font-weight: 400;
  margin-top: 3px;
  letter-spacing: -0.01em;
}

/* Header Right Badges & Buttons */
.head-count-badge {
  font-size: 12px;
  font-weight: 600;
  color: #85591c;
  background-color: #fcf6eb;
  border: 1px solid #ebd9be;
  padding: 6px 12px;
  border-radius: 8px;
  display: inline-flex;
  align-items: center;
}

.btn-action-outline {
  height: 38px !important;
  border: 1px solid #e2e8f0 !important;
  background-color: #ffffff !important;
  color: #334151 !important;
  font-family: "Plus Jakarta Sans", "Inter", sans-serif !important;
  font-size: 13px !important;
  font-weight: 600 !important;
  text-transform: none !important;
  border-radius: 8px !important;
  padding: 0 14px !important;
  letter-spacing: -0.01em !important;
  transition: all 0.2s ease !important;
}

.btn-action-outline .btn-icon {
  color: #64748b !important;
  transition: color 0.2s ease;
}

.btn-action-outline:hover {
  border-color: #9a6b27 !important;
  color: #9a6b27 !important;
  background-color: #fdfaf6 !important;
  transform: translateY(-1px);
}

.btn-action-outline:hover .btn-icon {
  color: #9a6b27 !important;
}

.btn-icon-only {
  min-width: 38px !important;
  width: 38px !important;
  padding: 0 !important;
}

/* Cards shared */
.form-card,
.table-card {
  background-color: #ffffff;
  border: 1px solid #e8e4dc;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.03) !important;
  overflow: hidden;
}

.border-bottom {
  border-bottom: 1px solid #f1f5f9;
}

.border-top {
  border-top: 1px solid #f1f5f9;
}

/* Form Card Header */
.form-card-header {
  background: #fafaf9;
}

.header-icon-pill {
  width: 32px;
  height: 32px;
  border-radius: 8px;
  background: #fcf6eb;
  border: 1px solid #ecd8b8;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.section-title {
  font-family: "Plus Jakarta Sans", sans-serif;
  font-size: 14.5px;
  font-weight: 700;
  color: #0f172a;
  letter-spacing: -0.01em;
}

.section-sub {
  font-size: 12px;
  color: #64748b;
}

/* Form Inputs */
.form-field-label {
  display: block;
  font-size: 11px;
  font-weight: 700;
  color: #475569;
  letter-spacing: 0.04em;
  margin-bottom: 6px;
  text-transform: uppercase;
}

.required-asterisk {
  color: #ef4444;
}

.optional-hint {
  font-size: 10.5px;
  color: #94a3b8;
  font-weight: 400;
  text-transform: none;
}

.modern-input-field >>> .v-input__slot {
  min-height: 40px !important;
  height: 40px !important;
  font-size: 13.5px !important;
  padding: 0 12px !important;
  border-radius: 8px !important;
  background-color: #ffffff !important;
  border-color: #e2e8f0 !important;
}

.modern-input-field >>> input {
  font-family: "Plus Jakarta Sans", "Inter", sans-serif !important;
  font-size: 13px !important;
  color: #0f172a !important;
  letter-spacing: -0.01em !important;
}

.modern-input-field >>> fieldset {
  border-color: #e2e8f0 !important;
}

.modern-input-field:hover >>> fieldset {
  border-color: #cbd5e1 !important;
}

.modern-input-field.v-input--is-focused >>> fieldset {
  border-color: #9a6b27 !important;
}

.modern-input-field >>> .v-input__prepend-inner {
  margin-top: 8px !important;
  margin-right: 6px !important;
}

.modern-input-field >>> .v-icon {
  font-size: 17px !important;
  color: #94a3b8 !important;
}

.modern-select >>> .v-select__selection {
  font-family: "Plus Jakarta Sans", "Inter", sans-serif !important;
  font-size: 13px !important;
  color: #0f172a !important;
  font-weight: 500 !important;
  letter-spacing: -0.01em !important;
}

.btn-add-head {
  height: 40px !important;
  background: linear-gradient(135deg, #b9893f 0%, #875c24 100%) !important;
  color: #ffffff !important;
  font-family: "Plus Jakarta Sans", "Inter", sans-serif !important;
  font-size: 13.5px !important;
  font-weight: 600 !important;
  text-transform: none !important;
  border-radius: 9px !important;
  padding: 0 18px !important;
  letter-spacing: -0.01em !important;
  box-shadow: 0 4px 14px rgba(154, 106, 44, 0.28) !important;
  transition: all 0.25s ease !important;
}

.btn-add-head:hover {
  background: linear-gradient(135deg, #c59449 0%, #956627 100%) !important;
  box-shadow: 0 6px 18px rgba(154, 106, 44, 0.38) !important;
  transform: translateY(-1px);
}

.w-100 {
  width: 100% !important;
}

/* Table Card Top Bar */
.table-card-top-bar {
  background-color: #ffffff;
}

.active-count-pill {
  font-size: 11.5px;
  font-weight: 600;
  color: #64748b;
  background-color: #f1f5f9;
  padding: 3px 8px;
  border-radius: 6px;
}

.filter-type-select {
  width: 140px;
}

.filter-type-select >>> .v-input__slot {
  min-height: 36px !important;
  height: 36px !important;
  font-size: 12.5px !important;
  border-radius: 8px !important;
  background-color: #f8fafc !important;
  border-color: #e2e8f0 !important;
}

.filter-search-input {
  width: 220px;
}

.filter-search-input >>> .v-input__slot {
  min-height: 36px !important;
  height: 36px !important;
  font-size: 12.5px !important;
  border-radius: 8px !important;
  background-color: #f8fafc !important;
  border-color: #e2e8f0 !important;
}

.filter-search-input >>> input {
  font-size: 12.5px !important;
  font-family: "Plus Jakarta Sans", sans-serif !important;
}

/* Custom Table */
.table-responsive {
  width: 100%;
  overflow-x: auto;
}

.custom-table {
  width: 100%;
  border-collapse: collapse;
  text-align: left;
}

.custom-table thead {
  background-color: #fafaf9;
}

.custom-table th {
  padding: 12px 16px;
  font-size: 11px;
  font-weight: 600;
  color: #64748b;
  letter-spacing: 0.05em;
  text-transform: uppercase;
  white-space: nowrap;
  border-bottom: 1px solid #f1f5f9;
  user-select: none;
  font-family: "Plus Jakarta Sans", "Inter", sans-serif;
}

.custom-table td {
  padding: 12px 16px;
  font-size: 13px;
  color: #1e293b;
  border-bottom: 1px solid #f8fafc;
  vertical-align: middle;
  font-family: "Plus Jakarta Sans", "Inter", sans-serif;
}

.table-row {
  transition: background-color 0.12s ease;
}

.table-row:hover {
  background-color: #fafaf9;
}

/* Column Widths */
.col-sno {
  width: 50px;
  min-width: 44px;
}

.col-name {
  min-width: 200px;
}

.col-code {
  width: 140px;
}

.col-type {
  width: 140px;
}

.col-action {
  width: 120px;
}

.font-mono {
  font-variant-numeric: tabular-nums;
}

.text-muted {
  color: #94a3b8 !important;
  font-size: 12px;
}

.text-muted-dash {
  color: #cbd5e1;
  font-size: 14px;
}

/* Account Name Cell */
.account-icon-box {
  width: 32px;
  height: 32px;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.icon-box-asset { background-color: #ecfdf5; border: 1px solid #d1fae5; }
.icon-box-liability { background-color: #fffbeb; border: 1px solid #fef3c7; }
.icon-box-capital { background-color: #f5f3ff; border: 1px solid #ede9fe; }
.icon-box-income { background-color: #f0fdfa; border: 1px solid #ccfbf1; }
.icon-box-expense { background-color: #fff1f2; border: 1px solid #ffe4e6; }
.icon-box-default { background-color: #f8fafc; border: 1px solid #e2e8f0; }

.account-name-text {
  font-size: 13.5px;
  font-weight: 600;
  color: #0f172a;
  line-height: 1.3;
}

.account-code-sub {
  font-size: 11px;
  color: #64748b;
  font-family: monospace;
}

/* Code Badge */
.code-badge {
  display: inline-block;
  font-size: 11.5px;
  font-weight: 600;
  color: #475569;
  background-color: #f1f5f9;
  border: 1px solid #e2e8f0;
  padding: 3px 8px;
  border-radius: 6px;
  letter-spacing: 0.02em;
}

/* Type Badge */
.type-badge {
  display: inline-flex;
  align-items: center;
  font-size: 11.5px;
  font-weight: 600;
  padding: 3px 10px;
  border-radius: 9999px;
  letter-spacing: -0.01em;
  white-space: nowrap;
}

.type-dot {
  width: 6px;
  height: 6px;
  border-radius: 50%;
  margin-right: 6px;
  display: inline-block;
  flex-shrink: 0;
}

.badge-asset { background-color: #ecfdf5; color: #047857; border: 1px solid #a7f3d0; }
.badge-liability { background-color: #fffbeb; color: #b45309; border: 1px solid #fde68a; }
.badge-capital { background-color: #f5f3ff; color: #6d28d9; border: 1px solid #ddd6fe; }
.badge-income { background-color: #f0fdfa; color: #0f766e; border: 1px solid #99f6e4; }
.badge-expense { background-color: #fff1f2; color: #be123c; border: 1px solid #fecdd3; }
.badge-default { background-color: #f8fafc; color: #475569; border: 1px solid #e2e8f0; }

.bg-asset { background-color: #059669; }
.bg-liability { background-color: #d97706; }
.bg-capital { background-color: #7c3aed; }
.bg-income { background-color: #0d9488; }
.bg-expense { background-color: #e11d48; }

/* Default badge */
.default-badge {
  display: inline-flex;
  align-items: center;
  font-size: 11.5px;
  font-weight: 600;
  color: #64748b;
  background-color: #f1f5f9;
  border: 1px solid #e2e8f0;
  padding: 4px 10px;
  border-radius: 6px;
  letter-spacing: -0.01em;
}

/* Delete button */
.btn-delete-action {
  transition: all 0.18s ease;
  border-radius: 8px;
}

.btn-delete-action:hover {
  background-color: #fef2f2 !important;
  transform: scale(1.08);
}

/* Empty State */
.empty-icon-circle {
  width: 64px;
  height: 64px;
  border-radius: 50%;
  background-color: #f8fafc;
  border: 1px solid #e2e8f0;
  display: flex;
  align-items: center;
  justify-content: center;
}

.empty-title {
  font-size: 15px;
  font-weight: 700;
  color: #1e293b;
}

.empty-desc {
  font-size: 13px;
  color: #64748b;
}

/* Table Footer */
.table-card-footer {
  background-color: #fafaf9;
}

.footer-summary-text {
  font-size: 12px;
  color: #64748b;
}

.legend-item {
  font-size: 11px;
  color: #64748b;
  display: inline-flex;
  align-items: center;
}

.gap-2 {
  gap: 8px;
}

.gap-3 {
  gap: 12px;
}

@media (max-width: 600px) {
  .filter-search-input,
  .filter-type-select {
    width: 100% !important;
    margin-right: 0 !important;
  }
}
</style>
